import React,
{
    useCallback,
    useEffect,
    useRef,
    useState
}
from 'react';
import
{
    normalizePath,
    replaceNavigate
}
from './Router';
import masterVolume from './masterVolume';
import
{
    createPreloadedAudio
}
from './audioPreload';
import
{
    isHalloweenMode
}
from './seasonalMode';
import RGLIcon from './RGLIcon';

const RGL_BASE_ICON_SIZE = 128;
const RGL_BACKGROUND_TO_ICON_RATIO = 520 / RGL_BASE_ICON_SIZE;
const RGL_BAR_HEIGHT_TO_ICON_RATIO = 140 / RGL_BASE_ICON_SIZE;
const RGL_BUTTON_CLUSTER_SHIFT_RATIO = 0 / RGL_BASE_ICON_SIZE; // shifts grouped top bar buttons to the right
const RGL_BUTTON_GROUP_CENTER_GAP_RATIO = 490 / RGL_BASE_ICON_SIZE; // gap between the two button groups
const RGL_BASE_CONTENT_GAP = 24;
const RGL_BASE_CONTENT_WRAP_GAP = 12;
const RGL_TOP_TAB_VIEWPORT_PADDING = 40;
const RGL_APPEAR_SFX_SRC = '/Audio/RGL-Appear.ogg';
const RGL_PRELOAD_NOTICE_DELAY_MS = 2000;
const RGL_ROUTE_BASE = '/RGL';
const RGL_AUDIO_CONFIG_URL = '/rglAudio.json';
const DEFAULT_RGL_PROFILE_STEAM_ID = '76561198346251523';
const DEFAULT_RGL_PAGE_MUSIC =
{
    music: '/Audio/RGL-Loop.ogg',
    intro: '',
    baseVolume: 0.45,
    musicDelayMs: 0,
};

function clamp01(value)
{
    return Math.max(0, Math.min(1, value));
}

function formatRglProfileUpdatedAt(timestamp)
{
    if (!timestamp) return 'Last updated: unknown';

    try
    {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) return 'Last updated: unknown';
        return `Last updated: ${date.toLocaleDateString(undefined,
        {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })}`;
    }
    catch (e)
    {
        return 'Last updated: unknown';
    }
}

function formatRglDivisionName(divisionName)
{
    const raw = typeof divisionName === 'string' ? divisionName.trim() : '';
    if (!raw) return '';
    return raw.replace(/^RGL\s*[-:]?\s*/i, '').trim();
}

function buildRglProfileApiUrl(steamId)
{
    return `https://api.rgl.gg/v0/profile/${steamId}`;
}

function buildRglProfileUrl(steamId)
{
    return `https://rgl.gg/Public/PlayerProfile?p=${steamId}`;
}

function getRglPageMusicBaseVolume(detail, fallback = 0.5)
{
    try
    {
        const raw = detail && typeof detail.baseVolume !== 'undefined' ? Number(detail.baseVolume) : NaN;
        return Number.isFinite(raw) ? clamp01(raw) : fallback;
    }
    catch (e)
    {
        return fallback;
    }
}

function getRglPageMusicStartDelayMs(detail, fallback = 0)
{
    try
    {
        const raw = detail && typeof detail.musicDelayMs !== 'undefined' ? Number(detail.musicDelayMs) : NaN;
        return Number.isFinite(raw) ? Math.max(0, raw) : fallback;
    }
    catch (e)
    {
        return fallback;
    }
}

function getRglPageMusicOnBeatEntry(detail)
{
    try
    {
        const enabled = !!(detail && (detail.onBeatEntry || detail.onBeatIntro));
        if (!enabled) return null;

        const bpmRaw = detail && typeof detail.onBeatBpm !== 'undefined' ? Number(detail.onBeatBpm) : NaN;
        const beatsRaw = detail && typeof detail.onBeatCount !== 'undefined' ? Number(detail.onBeatCount) : NaN;
        if (!Number.isFinite(bpmRaw) || bpmRaw <= 0) return null;
        if (!Number.isFinite(beatsRaw) || beatsRaw < 0) return null;

        return {
            bpm: bpmRaw,
            beats: beatsRaw,
        };
    }
    catch (e)
    {
        return null;
    }
}

function isRglRoute(path)
{
    const normalizedPath = normalizePath(path || RGL_ROUTE_BASE);
    return normalizedPath === RGL_ROUTE_BASE || normalizedPath.startsWith(`${RGL_ROUTE_BASE}/`);
}

function buildRglSelectionSlug(seasonId, weekId)
{
    if (!seasonId) return '';
    if (!weekId) return seasonId;
    return `${seasonId}-${weekId}`;
}

function buildRglSelectionPath(seasonId, weekId)
{
    const slug = buildRglSelectionSlug(seasonId, weekId);
    return slug ? `${RGL_ROUTE_BASE}/${slug}` : RGL_ROUTE_BASE;
}

function resolveRglSelection(seasonData, seasonId, weekId = null)
{
    const seasonList = seasonData && Array.isArray(seasonData.seasons) ? seasonData.seasons : [];
    const season = seasonList.find((entry) => entry && entry.id === seasonId);
    if (!season) return null;

    const seasonWeeks = Array.isArray(season.weeks) ? season.weeks : [];
    const matchingWeek = weekId ? seasonWeeks.find((week) => week && week.id === weekId) : null;
    const fallbackWeek = seasonWeeks.length > 0 ? seasonWeeks[0] : null;

    return {
        seasonId: season.id,
        weekId: matchingWeek ? matchingWeek.id : (fallbackWeek ? fallbackWeek.id : null),
        weeks: seasonWeeks,
    };
}

function getDefaultRglSelection(seasonData)
{
    const seasonList = seasonData && Array.isArray(seasonData.seasons) ? seasonData.seasons : [];
    if (!seasonList.length) return null;

    const latestSeason = seasonList[seasonList.length - 1];
    const latestSeasonWeeks = latestSeason && Array.isArray(latestSeason.weeks) ? latestSeason.weeks : [];
    const latestWeek = latestSeasonWeeks.length > 0 ? latestSeasonWeeks[latestSeasonWeeks.length - 1] : null;
    return resolveRglSelection(seasonData, latestSeason && latestSeason.id ? latestSeason.id : null, latestWeek && latestWeek.id ? latestWeek.id : null);
}

function getRglSelectionFromPath(path, seasonData)
{
    const normalizedPath = normalizePath(path || RGL_ROUTE_BASE);
    if (!isRglRoute(normalizedPath) || normalizedPath === RGL_ROUTE_BASE) return null;

    const slug = normalizedPath.slice(`${RGL_ROUTE_BASE}/`.length);
    if (!slug) return null;

    const seasonList = seasonData && Array.isArray(seasonData.seasons) ? seasonData.seasons : [];
    for (const season of seasonList)
    {
        if (!season || !season.id) continue;
        if (slug === season.id) return resolveRglSelection(seasonData, season.id);

        const seasonWeeks = Array.isArray(season.weeks) ? season.weeks : [];
        for (const week of seasonWeeks)
        {
            if (!week || !week.id) continue;
            if (slug === buildRglSelectionSlug(season.id, week.id)) return resolveRglSelection(seasonData, season.id, week.id);
        }

        if (slug.startsWith(`${season.id}-`)) return resolveRglSelection(seasonData, season.id);
    }

    return null;
}

export default function RGL()
{
    const topRef = useRef(null);
    const bottomRef = useRef(null);
    const [, setClosedEnded] = useState(false);
    const [swappedToOpen, setSwappedToOpen] = useState(false);
    const [showUI, setShowUI] = useState(false);
    const uiTimerRef = useRef(null);
    const preloadNoticeTimerRef = useRef(null);
    const audioRef = useRef(null); // loop audio
    const introAudioRef = useRef(null); // intro audio for the page music handoff
    const appearAudioRef = useRef(null); // one-shot logo slide-in SFX
    const appearAudioTimerRef = useRef(null);
    const audioFadeTimerRef = useRef(null);
    const audioFadeRafRef = useRef(null);
    const audioFadePendingRef = useRef(false);
    const rglStartDelayTimerRef = useRef(null);
    const rglBeatEntryTimerRef = useRef(null);
    const rglDesiredSrcRef = useRef(DEFAULT_RGL_PAGE_MUSIC.music);
    const rglIntroSrcRef = useRef(DEFAULT_RGL_PAGE_MUSIC.intro);
    const rglBaseVolumeRef = useRef(DEFAULT_RGL_PAGE_MUSIC.baseVolume);
    const rglStartDelayMsRef = useRef(DEFAULT_RGL_PAGE_MUSIC.musicDelayMs);
    const rglOnBeatEntryRef = useRef(null);
    const rglIntroPlayedRef = useRef(false);
    const rglLoopPrimedSrcRef = useRef(null);
    const rglIntroEndedHandlerRef = useRef(null);
    const rglPreloadPromiseRef = useRef(null);
    const rglRevealTokenRef = useRef(0);
    const rglAudioCtxRef = useRef(null);
    const rglWebMasterGainRef = useRef(null);
    const rglWebMusicGainRef = useRef(null);
    const rglWebBuffersRef = useRef(
    {
        intro: null,
        loop: null
    });
    const rglWebBufferSrcRef = useRef(
    {
        intro: '',
        loop: ''
    });
    const rglWebSourcesRef = useRef(
    {
        intro: null,
        loop: null
    });
    const rglWebLoadedRef = useRef(false);
    const rglWebUsingRef = useRef(false);
    const rglWebStopTimerRef = useRef(null);
    const rglWebMasterUnsubRef = useRef(null);
    const [moveToTop, setMoveToTop] = useState(false);
    const moveToTopTimerRef = useRef(null);
    const [showBar, setShowBar] = useState(false);
    const barTimerRef = useRef(null);
    const panelTimerRef = useRef(null);
    const contentRef = useRef(null);
    const [outroAnimating, setOutroAnimating] = useState(false);
    const outroTimerRef = useRef(null);
    const [activeInfoPanel, setActiveInfoPanel] = useState(null);
    const infoModalRef = useRef(null);
    const groupRef = useRef(null);
    const [moveUpPx, setMoveUpPx] = useState(0);
    const [iconSize, setIconSize] = useState(128);
    const [panelTopPx, setPanelTopPx] = useState(null);
    const [showPanel, setShowPanel] = useState(false);
    const [panelEverShown, setPanelEverShown] = useState(false);
    const [infoScale, setInfoScale] = useState(1);
    const [infoScaleReady, setInfoScaleReady] = useState(false);
    const [showPreloadNotice, setShowPreloadNotice] = useState(false);
    const [rglProfileSteamId, setRglProfileSteamId] = useState(DEFAULT_RGL_PROFILE_STEAM_ID);
    const [rglProfileSearchInput, setRglProfileSearchInput] = useState('');
    const [rglProfileState, setRglProfileState] = useState(
    {
        status: 'idle',
        data: null,
        error: ''
    });
    const rglProfileStateRef = useRef(
    {
        status: 'idle',
        data: null,
        error: ''
    });
    const topBarLayoutRef = useRef(null);
    const [topBarLayoutWidth, setTopBarLayoutWidth] = useState(0);
    const backgroundPlateWidth = Math.round(iconSize * RGL_BACKGROUND_TO_ICON_RATIO);
    const overlayScale = Math.max(0.72, Math.min(1, iconSize / RGL_BASE_ICON_SIZE));
    const barHeight = Math.round(iconSize * RGL_BAR_HEIGHT_TO_ICON_RATIO);
    const barHalfHeight = barHeight / 2;
    const barPaddingX = 24;
    const topBarButtonsShiftPx = Math.round(iconSize * RGL_BUTTON_CLUSTER_SHIFT_RATIO);
    const topBarGroupGap = Math.max(8, Math.round(14 * overlayScale));
    const topBarCenterGap = Math.max(18, Math.round(iconSize * RGL_BUTTON_GROUP_CENTER_GAP_RATIO));
    const rglTitleFontSize = `clamp(${Math.round(28 * overlayScale)}px, ${Number((7 * overlayScale).toFixed(3))}vw, ${Math.round(60 * overlayScale)}px)`;
    const viewportWidth = typeof window !== 'undefined' ? (window.innerWidth || document.documentElement.clientWidth || 0) : 0;
    const topTabAvailableWidth = Math.max(0, viewportWidth - RGL_TOP_TAB_VIEWPORT_PADDING);
    const topTabNaturalWidth = Math.max(backgroundPlateWidth, topBarLayoutWidth + (Math.abs(topBarButtonsShiftPx) * 2));
    const topTabFitScale = topTabNaturalWidth > 0 ? Math.min(1, topTabAvailableWidth / topTabNaturalWidth) : 1;

    // Week/details panel state (loaded from JSON)
    const [seasons, setSeasons] = useState([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState(null);
    const [weeks, setWeeks] = useState([]);
    const [selectedWeekId, setSelectedWeekId] = useState(null);
    const [seasonData, setSeasonData] = useState(null);

    const syncSelectionFromPath = useCallback((data) =>
    {
        const nextSelection = getRglSelectionFromPath(window.location.pathname, data) || getDefaultRglSelection(data);
        if (!nextSelection) return;

        setSelectedSeasonId((prev) => (prev === nextSelection.seasonId ? prev : nextSelection.seasonId));
        setWeeks(nextSelection.weeks);
        setSelectedWeekId((prev) => (prev === nextSelection.weekId ? prev : nextSelection.weekId));
    }, []);

    const applyRglPageMusicConfig = useCallback((detail) =>
    {
        const pageMusic = detail && typeof detail === 'object' ?
            {
                ...DEFAULT_RGL_PAGE_MUSIC,
                ...detail
            } :
            DEFAULT_RGL_PAGE_MUSIC;
        rglDesiredSrcRef.current = pageMusic.music || DEFAULT_RGL_PAGE_MUSIC.music;
        rglIntroSrcRef.current = pageMusic.intro || '';
        rglBaseVolumeRef.current = getRglPageMusicBaseVolume(pageMusic, DEFAULT_RGL_PAGE_MUSIC.baseVolume);
        rglStartDelayMsRef.current = getRglPageMusicStartDelayMs(pageMusic, DEFAULT_RGL_PAGE_MUSIC.musicDelayMs);
        rglOnBeatEntryRef.current = getRglPageMusicOnBeatEntry(pageMusic);
        rglLoopPrimedSrcRef.current = null;
        rglPreloadPromiseRef.current = null;
        rglWebLoadedRef.current = false;
        rglWebBuffersRef.current = {
            intro: null,
            loop: null
        };
        rglWebBufferSrcRef.current = {
            intro: '',
            loop: ''
        };
    }, []);

    const clearRglPreloadNotice = useCallback(() =>
    {
        if (preloadNoticeTimerRef.current)
        {
            clearTimeout(preloadNoticeTimerRef.current);
            preloadNoticeTimerRef.current = null;
        }
        setShowPreloadNotice(false);
    }, []);

    const clearPendingRglStart = useCallback(() =>
    {
        if (rglStartDelayTimerRef.current)
        {
            clearTimeout(rglStartDelayTimerRef.current);
            rglStartDelayTimerRef.current = null;
        }
        if (rglBeatEntryTimerRef.current)
        {
            clearTimeout(rglBeatEntryTimerRef.current);
            rglBeatEntryTimerRef.current = null;
        }
    }, []);

    const detachRglIntroEndedHandler = useCallback(() =>
    {
        const introAudio = introAudioRef.current;
        const handler = rglIntroEndedHandlerRef.current;
        if (introAudio && handler)
        {
            try
            {
                introAudio.removeEventListener('ended', handler);
            }
            catch (e)
            {}
        }
        rglIntroEndedHandlerRef.current = null;
    }, []);

    const createRglAudioElement = useCallback((src, baseVolume, loop) =>
    {
        return createPreloadedAudio(src,
        {
            baseVolume: Number.isFinite(baseVolume) ? clamp01(baseVolume) : DEFAULT_RGL_PAGE_MUSIC.baseVolume,
            loop,
            applyMaster: true,
        });
    }, []);

    const ensureRglAudioContext = useCallback(() =>
    {
        try
        {
            const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextCtor) return null;

            if (!rglAudioCtxRef.current)
            {
                const ctx = new AudioContextCtor();
                const masterGain = ctx.createGain();
                masterGain.gain.value = masterVolume.getMasterVolume();
                masterGain.connect(ctx.destination);

                const musicGain = ctx.createGain();
                musicGain.gain.value = Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume;
                musicGain.connect(masterGain);

                rglAudioCtxRef.current = ctx;
                rglWebMasterGainRef.current = masterGain;
                rglWebMusicGainRef.current = musicGain;
                rglWebMasterUnsubRef.current = masterVolume.subscribe((nextVolume) =>
                {
                    try
                    {
                        const audioCtx = rglAudioCtxRef.current;
                        const gainNode = rglWebMasterGainRef.current;
                        if (!audioCtx || !gainNode) return;
                        gainNode.gain.setValueAtTime(nextVolume, audioCtx.currentTime);
                    }
                    catch (e)
                    {}
                });
            }

            return rglAudioCtxRef.current;
        }
        catch (e)
        {
            return null;
        }
    }, []);

    const preloadRglMediaElement = useCallback((mediaEl) =>
    {
        if (!mediaEl) return Promise.resolve();
        if (mediaEl.readyState >= 4) return Promise.resolve();

        return new Promise((resolve) =>
        {
            let finished = false;
            const finish = () =>
            {
                if (finished) return;
                finished = true;
                try
                {
                    mediaEl.removeEventListener('canplaythrough', finish);
                }
                catch (e)
                {}
                try
                {
                    mediaEl.removeEventListener('loadeddata', finish);
                }
                catch (e)
                {}
                try
                {
                    mediaEl.removeEventListener('error', finish);
                }
                catch (e)
                {}
                resolve();
            };

            try
            {
                mediaEl.addEventListener('canplaythrough', finish);
            }
            catch (e)
            {}
            try
            {
                mediaEl.addEventListener('loadeddata', finish);
            }
            catch (e)
            {}
            try
            {
                mediaEl.addEventListener('error', finish);
            }
            catch (e)
            {}
            try
            {
                mediaEl.load();
            }
            catch (e)
            {
                finish();
            }
        });
    }, []);

    const preloadRglWebAudio = useCallback(async () =>
    {
        const loopSrc = rglDesiredSrcRef.current || '';
        const introSrc = rglIntroSrcRef.current || '';
        if (!loopSrc) return false;

        const ctx = ensureRglAudioContext();
        if (!ctx) return false;

        if (
            rglWebLoadedRef.current &&
            rglWebBufferSrcRef.current.loop === loopSrc &&
            rglWebBufferSrcRef.current.intro === introSrc &&
            rglWebBuffersRef.current.loop &&
            (!introSrc || rglWebBuffersRef.current.intro)
        )
        {
            return true;
        }

        try
        {
            const fetchAndDecode = async (src) =>
            {
                if (!src) return null;
                const res = await fetch(src);
                if (!res.ok) throw new Error(`Failed to fetch RGL audio: ${src}`);
                const arr = await res.arrayBuffer();
                return await ctx.decodeAudioData(arr);
            };

            const [loopBuffer, introBuffer] = await Promise.all([
                fetchAndDecode(loopSrc),
                fetchAndDecode(introSrc),
            ]);

            rglWebBuffersRef.current = {
                intro: introBuffer,
                loop: loopBuffer
            };
            rglWebBufferSrcRef.current = {
                intro: introSrc,
                loop: loopSrc
            };
            rglWebLoadedRef.current = !!loopBuffer;
            return !!loopBuffer;
        }
        catch (err)
        {
            console.warn('Failed to preload RGL WebAudio buffers', err);
            rglWebBuffersRef.current = {
                intro: null,
                loop: null
            };
            rglWebBufferSrcRef.current = {
                intro: '',
                loop: ''
            };
            rglWebLoadedRef.current = false;
            return false;
        }
    }, [ensureRglAudioContext]);

    const ensureRglLoopAudio = useCallback(() =>
    {
        const desiredSrc = rglDesiredSrcRef.current;
        if (!desiredSrc) return null;

        if (!audioRef.current)
        {
            audioRef.current = createRglAudioElement(desiredSrc, rglBaseVolumeRef.current, true);
        }

        const loopAudio = audioRef.current;
        try
        {
            if (loopAudio.src && !loopAudio.src.includes(desiredSrc))
            {
                loopAudio.src = desiredSrc;
                loopAudio.currentTime = 0;
            }
        }
        catch (e)
        {}

        loopAudio.loop = true;
        loopAudio._baseVolume = Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume;
        try
        {
            masterVolume.applyToElement(loopAudio);
        }
        catch (e)
        {}
        return loopAudio;
    }, [createRglAudioElement]);

    const ensureRglIntroAudio = useCallback(() =>
    {
        const introSrc = rglIntroSrcRef.current;
        if (!introSrc) return null;

        if (!introAudioRef.current)
        {
            introAudioRef.current = createRglAudioElement(introSrc, rglBaseVolumeRef.current, false);
        }

        const introAudio = introAudioRef.current;
        try
        {
            if (introAudio.src && !introAudio.src.includes(introSrc))
            {
                introAudio.src = introSrc;
                introAudio.currentTime = 0;
            }
        }
        catch (e)
        {}

        introAudio.loop = false;
        introAudio._baseVolume = Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume;
        try
        {
            masterVolume.applyToElement(introAudio);
        }
        catch (e)
        {}
        return introAudio;
    }, [createRglAudioElement]);

    const ensureRglAppearAudio = useCallback(() =>
    {
        if (!appearAudioRef.current)
        {
            appearAudioRef.current = createRglAudioElement(RGL_APPEAR_SFX_SRC, 1.0, false);
        }

        const appearAudio = appearAudioRef.current;
        appearAudio.loop = false;
        appearAudio._baseVolume = 1.0;
        try
        {
            masterVolume.applyToElement(appearAudio);
        }
        catch (e)
        {}
        return appearAudio;
    }, [createRglAudioElement]);

    const preloadRglAudioAssets = useCallback(() =>
    {
        if (rglPreloadPromiseRef.current) return rglPreloadPromiseRef.current;

        const preloadPromise = Promise.all([
            preloadRglWebAudio(),
            preloadRglMediaElement(ensureRglLoopAudio()),
            preloadRglMediaElement(ensureRglIntroAudio()),
            preloadRglMediaElement(ensureRglAppearAudio()),
        ]).catch((err) =>
        {
            console.warn('Failed to fully preload RGL audio assets', err);
        });

        rglPreloadPromiseRef.current = preloadPromise;
        return preloadPromise;
    }, [ensureRglAppearAudio, ensureRglIntroAudio, ensureRglLoopAudio, preloadRglMediaElement, preloadRglWebAudio]);

    const stopRglWebPlayback = useCallback((resetGain = true) =>
    {
        if (rglWebStopTimerRef.current)
        {
            clearTimeout(rglWebStopTimerRef.current);
            rglWebStopTimerRef.current = null;
        }

        ['intro', 'loop'].forEach((key) =>
        {
            const source = rglWebSourcesRef.current[key];
            if (!source) return;
            try
            {
                source.onended = null;
            }
            catch (e)
            {}
            try
            {
                source.stop();
            }
            catch (e)
            {}
            try
            {
                source.disconnect();
            }
            catch (e)
            {}
            rglWebSourcesRef.current[key] = null;
        });

        if (resetGain)
        {
            try
            {
                const ctx = rglAudioCtxRef.current;
                const gainNode = rglWebMusicGainRef.current;
                if (ctx && gainNode)
                {
                    const nextBase = Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume;
                    gainNode.gain.cancelScheduledValues(ctx.currentTime);
                    gainNode.gain.setValueAtTime(nextBase, ctx.currentTime);
                }
            }
            catch (e)
            {}
        }

        rglWebUsingRef.current = false;
    }, []);

    const startRglWithWebAudio = useCallback(async () =>
    {
        const ready = await preloadRglWebAudio();
        const ctx = ensureRglAudioContext();
        if (!ready || !ctx) return false;

        try
        {
            if (ctx.state === 'suspended') await ctx.resume();
        }
        catch (e)
        {}

        const loopBuffer = rglWebBuffersRef.current.loop;
        const introBuffer = rglWebBuffersRef.current.intro;
        const gainNode = rglWebMusicGainRef.current;
        if (!loopBuffer || !gainNode) return false;

        stopRglWebPlayback(false);

        const baseVolume = Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume;
        gainNode.gain.cancelScheduledValues(ctx.currentTime);
        gainNode.gain.setValueAtTime(baseVolume, ctx.currentTime);

        const onBeatEntry = rglOnBeatEntryRef.current;
        const startAt = ctx.currentTime + 0.05;
        const makeSource = (buffer, loop, when, onEnded = null) =>
        {
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.loop = loop;
            source.connect(gainNode);
            if (onEnded) source.onended = onEnded;
            source.start(when);
            return source;
        };

        if (introBuffer && !rglIntroPlayedRef.current)
        {
            rglIntroPlayedRef.current = true;
            const beatDelaySeconds = onBeatEntry ?
                Math.max(0, (60 / onBeatEntry.bpm) * Math.max(0, onBeatEntry.beats - 1)) :
                Math.max(0, introBuffer.duration);
            const loopStartAt = startAt + beatDelaySeconds;

            const introSource = makeSource(introBuffer, false, startAt, () =>
            {
                if (rglWebSourcesRef.current.intro === introSource)
                {
                    rglWebSourcesRef.current.intro = null;
                }
            });
            const loopSource = makeSource(loopBuffer, true, loopStartAt);

            rglWebSourcesRef.current.intro = introSource;
            rglWebSourcesRef.current.loop = loopSource;
            rglWebUsingRef.current = true;
            return true;
        }

        const loopSource = makeSource(loopBuffer, true, startAt);
        rglWebSourcesRef.current.intro = null;
        rglWebSourcesRef.current.loop = loopSource;
        rglWebUsingRef.current = true;
        return true;
    }, [ensureRglAudioContext, preloadRglWebAudio, stopRglWebPlayback]);

    const primeRglLoopForBeatEntry = useCallback(async () =>
    {
        const desiredSrc = rglDesiredSrcRef.current;
        if (!desiredSrc) return;
        if (rglLoopPrimedSrcRef.current === desiredSrc) return;

        try
        {
            const loopAudio = ensureRglLoopAudio();
            if (!loopAudio) return;
            try
            {
                loopAudio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                loopAudio.muted = true;
            }
            catch (e)
            {}
            await loopAudio.play();
            try
            {
                loopAudio.pause();
            }
            catch (e)
            {}
            try
            {
                loopAudio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                loopAudio.muted = false;
            }
            catch (e)
            {}
            rglLoopPrimedSrcRef.current = desiredSrc;
        }
        catch (err)
        {
            try
            {
                if (audioRef.current) audioRef.current.muted = false;
            }
            catch (e)
            {}
            console.warn('Failed to prime RGL loop for beat entry', err);
            rglLoopPrimedSrcRef.current = null;
        }
    }, [ensureRglLoopAudio]);

    const playRglLoop = useCallback(async () =>
    {
        const loopAudio = ensureRglLoopAudio();
        if (!loopAudio) return;

        try
        {
            try
            {
                loopAudio.muted = false;
            }
            catch (e)
            {}
            loopAudio.currentTime = 0;
            await loopAudio.play();
            rglLoopPrimedSrcRef.current = rglDesiredSrcRef.current;
        }
        catch (err)
        {
            console.warn('Failed to play RGL loop', err);
        }
    }, [ensureRglLoopAudio]);

    const stopRglAudio = useCallback(() =>
    {
        clearPendingRglStart();
        detachRglIntroEndedHandler();
        rglRevealTokenRef.current += 1;
        stopRglWebPlayback();

        if (appearAudioTimerRef.current)
        {
            clearTimeout(appearAudioTimerRef.current);
            appearAudioTimerRef.current = null;
        }

        if (audioRef.current)
        {
            try
            {
                audioRef.current.pause();
                try
                {
                    audioRef.current.muted = false;
                }
                catch (e)
                {}
                audioRef.current.currentTime = 0;
                try
                {
                    if (typeof audioRef.current._masterVolumeUnsub === 'function') audioRef.current._masterVolumeUnsub();
                }
                catch (e)
                {}
                try
                {
                    audioRef.current.src = '';
                }
                catch (e)
                {}
            }
            catch (e)
            {}
            audioRef.current = null;
        }

        if (introAudioRef.current)
        {
            try
            {
                introAudioRef.current.pause();
                introAudioRef.current.currentTime = 0;
                try
                {
                    if (typeof introAudioRef.current._masterVolumeUnsub === 'function') introAudioRef.current._masterVolumeUnsub();
                }
                catch (e)
                {}
                try
                {
                    introAudioRef.current.src = '';
                }
                catch (e)
                {}
            }
            catch (e)
            {}
            introAudioRef.current = null;
        }

        if (appearAudioRef.current)
        {
            try
            {
                appearAudioRef.current.pause();
                appearAudioRef.current.currentTime = 0;
                try
                {
                    if (typeof appearAudioRef.current._masterVolumeUnsub === 'function') appearAudioRef.current._masterVolumeUnsub();
                }
                catch (e)
                {}
                try
                {
                    appearAudioRef.current.src = '';
                }
                catch (e)
                {}
            }
            catch (e)
            {}
            appearAudioRef.current = null;
        }

        rglIntroPlayedRef.current = false;
        rglLoopPrimedSrcRef.current = null;
        rglPreloadPromiseRef.current = null;
    }, [clearPendingRglStart, detachRglIntroEndedHandler, stopRglWebPlayback]);

    const startRglWithIntroIfNeeded = useCallback(async () =>
    {
        try
        {
            const startedWithWebAudio = await startRglWithWebAudio();
            if (startedWithWebAudio) return;

            const introSrc = rglIntroSrcRef.current;
            if (introSrc && !rglIntroPlayedRef.current)
            {
                const introAudio = ensureRglIntroAudio();
                const onBeatEntry = rglOnBeatEntryRef.current;
                if (!introAudio) return;

                rglIntroPlayedRef.current = true;
                let transitioned = false;

                const cleanupIntroState = (
                {
                    stopIntro = false,
                    clearBeatTimer = true
                } = {}) =>
                {
                    if (clearBeatTimer && rglBeatEntryTimerRef.current)
                    {
                        clearTimeout(rglBeatEntryTimerRef.current);
                        rglBeatEntryTimerRef.current = null;
                    }
                    try
                    {
                        introAudio.removeEventListener('ended', onEnded);
                    }
                    catch (e)
                    {}
                    if (stopIntro)
                    {
                        try
                        {
                            introAudio.pause();
                        }
                        catch (e)
                        {}
                        try
                        {
                            introAudio.currentTime = 0;
                        }
                        catch (e)
                        {}
                    }
                    if (introAudioRef.current === introAudio)
                    {
                        introAudioRef.current = null;
                    }
                    rglIntroEndedHandlerRef.current = null;
                };

                const transitionToLoop = () =>
                {
                    if (transitioned) return;
                    transitioned = true;
                    try
                    {
                        playRglLoop();
                    }
                    catch (e)
                    {}
                    if (!onBeatEntry)
                    {
                        cleanupIntroState(
                        {
                            stopIntro: true
                        });
                    }
                };

                const onEnded = () =>
                {
                    if (onBeatEntry)
                    {
                        cleanupIntroState(
                        {
                            stopIntro: false,
                            clearBeatTimer: false
                        });
                        return;
                    }
                    transitionToLoop();
                };

                detachRglIntroEndedHandler();
                rglIntroEndedHandlerRef.current = onEnded;
                try
                {
                    introAudio.addEventListener('ended', onEnded);
                }
                catch (e)
                {}
                try
                {
                    introAudio.currentTime = 0;
                }
                catch (e)
                {}

                const introStartMs = performance.now();
                const playPromise = introAudio.play();

                if (onBeatEntry)
                {
                    try
                    {
                        primeRglLoopForBeatEntry();
                    }
                    catch (e)
                    {}
                    const msPerBeat = 60000 / onBeatEntry.bpm;
                    const beatTargetDelayMs = Math.max(0, msPerBeat * Math.max(0, onBeatEntry.beats - 1));
                    const elapsedBeforeScheduleMs = Math.max(0, performance.now() - introStartMs);
                    const remainingBeatDelayMs = Math.max(0, beatTargetDelayMs - elapsedBeforeScheduleMs);
                    if (remainingBeatDelayMs <= 0)
                    {
                        transitionToLoop();
                    }
                    else
                    {
                        rglBeatEntryTimerRef.current = setTimeout(() =>
                        {
                            rglBeatEntryTimerRef.current = null;
                            transitionToLoop();
                        }, remainingBeatDelayMs);
                    }
                }

                try
                {
                    await playPromise;
                }
                catch (e)
                {
                    if (rglBeatEntryTimerRef.current)
                    {
                        clearTimeout(rglBeatEntryTimerRef.current);
                        rglBeatEntryTimerRef.current = null;
                    }
                    rglIntroPlayedRef.current = false;
                    return;
                }
                return;
            }
        }
        catch (e)
        {}

        if (rglDesiredSrcRef.current)
        {
            playRglLoop();
        }
    }, [detachRglIntroEndedHandler, ensureRglIntroAudio, playRglLoop, primeRglLoopForBeatEntry, startRglWithWebAudio]);

    const queueRglStart = useCallback(() =>
    {
        clearPendingRglStart();
        rglIntroPlayedRef.current = false;

        const delayMs = Number.isFinite(rglStartDelayMsRef.current) ? Math.max(0, rglStartDelayMsRef.current) : 0;
        if (delayMs <= 0)
        {
            startRglWithIntroIfNeeded();
            return;
        }

        rglStartDelayTimerRef.current = setTimeout(() =>
        {
            rglStartDelayTimerRef.current = null;
            startRglWithIntroIfNeeded();
        }, delayMs);
    }, [clearPendingRglStart, startRglWithIntroIfNeeded]);

    // Load RGL audio mode and season data from JSON
    useEffect(() =>
    {
        let cancelled = false;
        const load = async () =>
        {
            try
            {
                const [audioResponse, seasonResponse] = await Promise.all([
                    fetch(RGL_AUDIO_CONFIG_URL),
                    fetch('/rglSeasons.json'),
                ]);
                const audioConfig = await audioResponse.json();
                const data = await seasonResponse.json();
                if (!cancelled && data && Array.isArray(data.seasons))
                {
                    const audioMode = isHalloweenMode() ? 'halloween' : 'default';
                    const pageMusic = audioConfig && audioConfig[audioMode] ? audioConfig[audioMode] : DEFAULT_RGL_PAGE_MUSIC;
                    applyRglPageMusicConfig(pageMusic);
                    try
                    {
                        preloadRglAudioAssets();
                    }
                    catch (e)
                    {}
                    setSeasonData(data);
                    setSeasons(data.seasons.map(s => (
                    {
                        id: s.id,
                        label: s.label
                    })));
                    syncSelectionFromPath(data);
                }
            }
            catch (e)
            {
                try
                {
                    preloadRglAudioAssets();
                }
                catch (err)
                {}
                if (!cancelled) console.error('Failed to load RGL seasons:', e);
            }
        };
        load();
        return () =>
        {
            cancelled = true;
        };
    }, [applyRglPageMusicConfig, preloadRglAudioAssets, syncSelectionFromPath]);

    // Update week list when season changes
    useEffect(() =>
    {
        if (!seasonData || !selectedSeasonId) return;
        const season = seasonData.seasons.find(s => s.id === selectedSeasonId);
        if (season && season.weeks)
        {
            setWeeks(season.weeks);
            const nextWeekId = season.weeks.some((week) => week && week.id === selectedWeekId) ?
                selectedWeekId :
                (season.weeks.length > 0 ? season.weeks[0].id : null);
            if (selectedWeekId !== nextWeekId)
            {
                setSelectedWeekId(nextWeekId);
            }
        }
    }, [selectedSeasonId, seasonData, selectedWeekId]);

    useEffect(() =>
    {
        if (!seasonData) return undefined;

        const handlePopState = () =>
        {
            syncSelectionFromPath(seasonData);
        };

        handlePopState();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [seasonData, syncSelectionFromPath]);

    useEffect(() =>
    {
        if (!seasonData || !selectedSeasonId) return;

        const resolvedSelection = resolveRglSelection(seasonData, selectedSeasonId, selectedWeekId);
        if (!resolvedSelection) return;

        const targetPath = buildRglSelectionPath(resolvedSelection.seasonId, resolvedSelection.weekId);
        const activePath = normalizePath(window.location.pathname || RGL_ROUTE_BASE);
        if (activePath === targetPath) return;

        replaceNavigate(targetPath);
    }, [seasonData, selectedSeasonId, selectedWeekId]);

    // keep the icon size sane across phones/ultrawide/odd aspect ratios
    useEffect(() =>
    {
        const update = () =>
        {
            const vw = window.innerWidth || document.documentElement.clientWidth || 0;
            const vh = window.innerHeight || document.documentElement.clientHeight || 0;
            const basis = Math.max(0, Math.min(vw, vh));
            // clamp: never tiny, never huge
            const next = Math.round(Math.max(72, Math.min(128, basis * 0.18)));
            setIconSize(prev => (prev === next ? prev : next));
        };

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [stopRglAudio]);

    useEffect(() =>
    {
        const top = topRef.current;
        const bottom = bottomRef.current;

        if (top)
        {
            try
            {
                top._baseVolume = typeof top._baseVolume === 'undefined' ? 1.0 : top._baseVolume;
            }
            catch (e)
            {}
            try
            {
                masterVolume.applyToElement(top);
            }
            catch (e)
            {}
            top.play().catch(() =>
            {});
        }

        if (bottom)
        {
            try
            {
                bottom._baseVolume = typeof bottom._baseVolume === 'undefined' ? 1.0 : bottom._baseVolume;
            }
            catch (e)
            {}
            try
            {
                masterVolume.applyToElement(bottom);
            }
            catch (e)
            {}
            // preload the opening video so it's ready to play when swapped
            try
            {
                bottom.load();
            }
            catch (e)
            {}
        }

        return () =>
        {
            try
            {
                if (top && top._masterVolumeUnsub) top._masterVolumeUnsub();
            }
            catch (e)
            {}
            try
            {
                if (bottom && bottom._masterVolumeUnsub) bottom._masterVolumeUnsub();
            }
            catch (e)
            {}
            if (uiTimerRef.current)
            {
                clearTimeout(uiTimerRef.current);
                uiTimerRef.current = null;
            }
            clearRglPreloadNotice();
            if (audioFadeTimerRef.current)
            {
                clearTimeout(audioFadeTimerRef.current);
                audioFadeTimerRef.current = null;
            }
            if (audioFadeRafRef.current)
            {
                cancelAnimationFrame(audioFadeRafRef.current);
                audioFadeRafRef.current = null;
            }
            stopRglAudio();
            if (moveToTopTimerRef.current)
            {
                clearTimeout(moveToTopTimerRef.current);
                moveToTopTimerRef.current = null;
            }
            if (barTimerRef.current)
            {
                clearTimeout(barTimerRef.current);
                barTimerRef.current = null;
            }
            if (panelTimerRef.current)
            {
                clearTimeout(panelTimerRef.current);
                panelTimerRef.current = null;
            }
            if (outroTimerRef.current)
            {
                clearTimeout(outroTimerRef.current);
                outroTimerRef.current = null;
            }
        };
    }, [clearRglPreloadNotice, stopRglAudio]);

    const onClosedEnded = () =>
    {
        setClosedEnded(true);
    };

    const onReturnClick = async () =>
    {
        const bottom = bottomRef.current;
        if (!bottom) return;
        clearRglPreloadNotice();
        // schedule audio fade after 1s, but do not let the effect immediately pause the audio
        audioFadePendingRef.current = true;
        if (audioFadeTimerRef.current)
        {
            clearTimeout(audioFadeTimerRef.current);
            audioFadeTimerRef.current = null;
        }
        audioFadeTimerRef.current = setTimeout(() =>
        {
            const activeAudio = [audioRef.current, introAudioRef.current].filter(Boolean);
            const ctx = rglAudioCtxRef.current;
            const webGain = rglWebMusicGainRef.current;
            const hasWebAudio = !!(rglWebUsingRef.current && ctx && webGain);
            if (!activeAudio.length && !hasWebAudio)
            {
                audioFadePendingRef.current = false;
                return;
            }
            const duration = 420;
            const start = performance.now();
            const startingVolumes = activeAudio.map((audio) =>
            {
                try
                {
                    return Number.isFinite(audio.volume) ? audio.volume : 1;
                }
                catch (e)
                {
                    return 1;
                }
            });
            if (hasWebAudio)
            {
                try
                {
                    const from = Number.isFinite(webGain.gain.value) ? webGain.gain.value : (Number.isFinite(rglBaseVolumeRef.current) ? clamp01(rglBaseVolumeRef.current) : DEFAULT_RGL_PAGE_MUSIC.baseVolume);
                    webGain.gain.cancelScheduledValues(ctx.currentTime);
                    webGain.gain.setValueAtTime(from, ctx.currentTime);
                    webGain.gain.linearRampToValueAtTime(0, ctx.currentTime + (duration / 1000));
                }
                catch (e)
                {}
            }
            const step = (now) =>
            {
                const t = Math.min(1, (now - start) / duration);
                activeAudio.forEach((audio, index) =>
                {
                    try
                    {
                        audio.volume = Math.max(0, Math.min(1, startingVolumes[index] * (1 - t)));
                    }
                    catch (e)
                    {}
                });
                if (t < 1)
                {
                    audioFadeRafRef.current = requestAnimationFrame(step);
                }
                else
                {
                    audioFadeRafRef.current = null;
                    stopRglAudio();
                    audioFadePendingRef.current = false;
                }
            };
            audioFadeRafRef.current = requestAnimationFrame(step);
        }, 1000);

        // remove top immediately and play bottom
        setSwappedToOpen(true);
        // no need to track this flag; removed to fix unused var warning
        setClosedEnded(false);
        // start outro animation: slide upper UI out while doors open
        setOutroAnimating(true);
        // hide UI after outro completes
        if (outroTimerRef.current)
        {
            clearTimeout(outroTimerRef.current);
            outroTimerRef.current = null;
        }
        outroTimerRef.current = setTimeout(() =>
        {
            setShowUI(false);
            setOutroAnimating(false);
            setShowPanel(false);
            setPanelEverShown(false);
        }, 320);
        bottom.currentTime = 0;
        try
        {
            bottom._baseVolume = typeof bottom._baseVolume === 'undefined' ? 1.0 : bottom._baseVolume;
        }
        catch (e)
        {}
        try
        {
            masterVolume.applyToElement(bottom);
        }
        catch (e)
        {}
        try
        {
            await bottom.play();
        }
        catch (e)
        {}
    };

    // play/pause RGL page music when the UI becomes visible/hidden
    useEffect(() =>
    {
        if (showUI && !swappedToOpen)
        {
            if (appearAudioTimerRef.current)
            {
                clearTimeout(appearAudioTimerRef.current);
                appearAudioTimerRef.current = null;
            }
            appearAudioTimerRef.current = setTimeout(() =>
            {
                const appearAudio = ensureRglAppearAudio();
                if (!appearAudio) return;
                try
                {
                    appearAudio.currentTime = 0;
                }
                catch (e)
                {}
                appearAudio.play().catch(() =>
                {});
                appearAudioTimerRef.current = null;
            }, 50);
            queueRglStart();
        }
        else
        {
            if (appearAudioTimerRef.current)
            {
                clearTimeout(appearAudioTimerRef.current);
                appearAudioTimerRef.current = null;
            }
            if (appearAudioRef.current)
            {
                try
                {
                    appearAudioRef.current.pause();
                    appearAudioRef.current.currentTime = 0;
                }
                catch (e)
                {}
            }
            clearPendingRglStart();
            if (audioFadePendingRef.current) return;
            stopRglAudio();
        }
    }, [clearPendingRglStart, ensureRglAppearAudio, queueRglStart, showUI, stopRglAudio, swappedToOpen]);

    // after the group moves to top, reveal a wide rectangle bar behind the assets
    useEffect(() =>
    {
        if (showUI && moveToTop && !swappedToOpen)
        {
            if (barTimerRef.current)
            {
                clearTimeout(barTimerRef.current);
                barTimerRef.current = null;
            }
            // wait for the move-to-top transition (~220ms) then show the bar
            barTimerRef.current = setTimeout(() =>
            {
                setShowBar(true);
            }, 240);
        }
        else if (outroAnimating)
        {
            // keep whatever state we were in so the bar can animate out with the group
            if (barTimerRef.current)
            {
                clearTimeout(barTimerRef.current);
                barTimerRef.current = null;
            }
        }
        else
        {
            if (barTimerRef.current)
            {
                clearTimeout(barTimerRef.current);
                barTimerRef.current = null;
            }
            setShowBar(false);
        }
    }, [showUI, moveToTop, swappedToOpen, outroAnimating]);

    // panel animation sequence: fade in after bar finishes animating in
    useEffect(() =>
    {
        if (showBar && !outroAnimating && !swappedToOpen)
        {
            if (panelTimerRef.current)
            {
                clearTimeout(panelTimerRef.current);
                panelTimerRef.current = null;
            }
            // bar anim is 240ms; wait a hair longer then fade in the panel
            panelTimerRef.current = setTimeout(() =>
            {
                setShowPanel(true);
                setPanelEverShown(true);
                panelTimerRef.current = null;
            }, 260);
            return;
        }

        if (!outroAnimating)
        {
            if (panelTimerRef.current)
            {
                clearTimeout(panelTimerRef.current);
                panelTimerRef.current = null;
            }
            setShowPanel(false);
            setPanelEverShown(false);
        }
    }, [showBar, outroAnimating, swappedToOpen]);

    // measure centered content width to reserve a spacer in the bar overlay
    useEffect(() =>
    {
        const update = () =>
        {
            const el = contentRef.current;
            const groupEl = groupRef.current;
            if (!el) return;
            try
            {
                // hopefully an offset that keeps the group visible
                // clamp so the group's top never goes off-screen i hope
                if (groupEl)
                {
                    const gRect = groupEl.getBoundingClientRect();
                    const safeTopMargin = 12; // px
                    const viewportH = window.innerHeight || document.documentElement.clientHeight || gRect.height;
                    const desiredPx = viewportH * 0.40;
                    const maxAllowedPx = Math.max(0, (viewportH / 2) - safeTopMargin - (gRect.height / 2));
                    const finalPx = Math.min(desiredPx, maxAllowedPx);
                    setMoveUpPx(Math.round(finalPx));

                    // Panel positioning: the bar is centered on the group's center and has height 140.
                    // Anchor the panel underneath the bar so it tracks the group's move-to-top.
                    const barBottomY = (gRect.top + (gRect.height / 2)) + barHalfHeight;

                    const desiredPanelTop = barBottomY + 8;
                    const minTop = 12;
                    const finalTop = Math.max(minTop, Math.min(desiredPanelTop, viewportH - 100));

                    setPanelTopPx(Math.round(finalTop));
                }
            }
            catch (e)
            {}
        };

        if (showUI && !swappedToOpen)
        {
            const id = setTimeout(update, 260);
            window.addEventListener('resize', update);
            return () =>
            {
                clearTimeout(id);
                window.removeEventListener('resize', update);
            };
        }
    }, [showUI, swappedToOpen, moveToTop, showBar, barHalfHeight]);

    useEffect(() =>
    {
        const update = () =>
        {
            const el = topBarLayoutRef.current;
            if (!el) return;

            try
            {
                const nextWidth = Math.round(el.offsetWidth || el.scrollWidth || 0);
                setTopBarLayoutWidth((prev) => (prev === nextWidth ? prev : nextWidth));
            }
            catch (e)
            {}
        };

        if (showUI && !swappedToOpen)
        {
            const id = setTimeout(update, 260);
            window.addEventListener('resize', update);
            return () =>
            {
                clearTimeout(id);
                window.removeEventListener('resize', update);
            };
        }
    }, [showUI, swappedToOpen, showBar, overlayScale]);

    // when UI is shown and assets have slid in, quickly move the group to the top
    useEffect(() =>
    {
        if (showUI && !swappedToOpen)
        {
            if (moveToTopTimerRef.current)
            {
                clearTimeout(moveToTopTimerRef.current);
                moveToTopTimerRef.current = null;
            }
            // initial slide-in completes, then move up after 0.5s
            moveToTopTimerRef.current = setTimeout(() =>
            {
                setMoveToTop(true);
            }, 1000);
        }
        else
        {
            if (moveToTopTimerRef.current)
            {
                clearTimeout(moveToTopTimerRef.current);
                moveToTopTimerRef.current = null;
            }
            setMoveToTop(false);
        }
    }, [showUI, swappedToOpen]);

    const onOpenEnded = () =>
    {
        window.dispatchEvent(new CustomEvent('rgl-open-finished'));
    };

    const infoModalPaddingPx = 20;

    useEffect(() =>
    {
        rglProfileStateRef.current = rglProfileState;
    }, [rglProfileState]);

    useEffect(() =>
    {
        if (!activeInfoPanel)
        {
            setInfoScale(1);
            setInfoScaleReady(false);
            return;
        }

        const modalEl = infoModalRef.current;
        if (!modalEl) return;

        const updateInfoScale = () =>
        {
            const viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
            const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
            const availableWidth = Math.max(0, viewportW - (infoModalPaddingPx * 2));
            const availableHeight = Math.max(0, viewportH - (infoModalPaddingPx * 2));

            const naturalWidth = modalEl.offsetWidth || 1;
            const naturalHeight = modalEl.offsetHeight || 1;
            const widthScale = availableWidth / naturalWidth;
            const heightScale = availableHeight / naturalHeight;
            const nextScale = Math.max(0.35, Math.min(1, widthScale, heightScale));

            setInfoScale(prev => (Math.abs(prev - nextScale) < 0.01 ? prev : nextScale));
            setInfoScaleReady(true);
        };

        const rafId = requestAnimationFrame(updateInfoScale);
        let resizeObserver = null;

        if (typeof ResizeObserver !== 'undefined')
        {
            resizeObserver = new ResizeObserver(() =>
            {
                requestAnimationFrame(updateInfoScale);
            });
            resizeObserver.observe(modalEl);
        }

        window.addEventListener('resize', updateInfoScale);

        return () =>
        {
            cancelAnimationFrame(rafId);
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', updateInfoScale);
        };
    }, [activeInfoPanel]);

    useEffect(() =>
    {
        if (activeInfoPanel !== 'profile') return undefined;
        if (rglProfileStateRef.current.status === 'loading' || rglProfileStateRef.current.status === 'success') return undefined;

        const controller = new AbortController();
        let cancelled = false;

        setRglProfileState(
        {
            status: 'loading',
            data: null,
            error: ''
        });

        fetch(buildRglProfileApiUrl(rglProfileSteamId),
        {
            signal: controller.signal,
        })
            .then((response) =>
            {
                if (!response.ok) throw new Error(`RGL profile request failed with ${response.status}`);
                return response.json();
            })
            .then((data) =>
            {
                if (cancelled) return;
                setRglProfileState(
                {
                    status: 'success',
                    data,
                    error: ''
                });
            })
            .catch((error) =>
            {
                if (cancelled || (error && error.name === 'AbortError')) return;
                setRglProfileState(
                {
                    status: 'error',
                    data: null,
                    error: error && error.message ? error.message : 'Failed to load RGL profile.',
                });
            });

        return () =>
        {
            cancelled = true;
            controller.abort();
        };
    }, [activeInfoPanel, rglProfileSteamId]);

    const rglProfileData = rglProfileState.data;
    const rglHighlanderTeam = rglProfileData && rglProfileData.currentTeams ? rglProfileData.currentTeams.highlander : null;
    const rglSixesTeam = rglProfileData && rglProfileData.currentTeams ? rglProfileData.currentTeams.sixes : null;
    const rglPrimaryTeam = rglHighlanderTeam || rglSixesTeam || null;
    const rglPrimaryTeamFormat = rglHighlanderTeam ? 'Highlander' : (rglSixesTeam ? 'Sixes' : '');
    const rglProfileUpdatedLabel = formatRglProfileUpdatedAt(rglProfileData && rglProfileData.updatedAt ? rglProfileData.updatedAt : '');
    const rglProfileStatus = rglProfileData && rglProfileData.status ? rglProfileData.status : null;
    const rglProfileProbationLabel = rglProfileState.status === 'success' ?
        ((rglProfileStatus && rglProfileStatus.isOnProbation) ? 'Yes' : 'None') :
        (rglProfileState.status === 'error' ? 'Unavailable' : 'Loading...');
    const rglProfileBanLabel = rglProfileState.status === 'success' ?
        ((rglProfileStatus && rglProfileStatus.isBanned) ? 'YES LMAO' : 'None') :
        (rglProfileState.status === 'error' ? 'Unavailable' : 'Loading...');

    const infoModalContent = activeInfoPanel === 'profile' ?
        {
            modalStyle:
            {
                maxWidth: 920,
                width: 'min(920px, 92vw)',
                padding: 0,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none'
            },
            customBody: ( <div style = {
                    {
                        boxSizing: 'border-box',
                        padding: 18,
                        borderRadius: 18,
                        background: 'linear-gradient(135deg, rgba(12,12,12,0.96), rgba(34,22,8,0.94))',
                        border: '1px solid rgba(255,145,0,0.4)',
                        boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
                        display: 'grid',
                        gridTemplateColumns: '180px minmax(0, 1fr)',
                        gap: 20,
                        alignItems: 'stretch',
                        minHeight: 180
                    }
                } >
                <div style = {
                    {
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 14,
                        background: 'linear-gradient(160deg, rgba(255,145,0,0.22), rgba(255,255,255,0.06))',
                        border: '1px solid rgba(255,255,255,0.1)',
                        width: 180,
                        height: 180,
                        aspectRatio: '1 / 1',
                        alignSelf: 'center',
                        justifySelf: 'center',
                        transform: 'translateX(75px)'
                    }
                } >
                {
                    rglProfileState.status === 'success' && rglProfileData && rglProfileData.avatar ? ( <img src = {
                            rglProfileData.avatar
                        }
                        alt = {
                            rglProfileData.name || 'RGL profile avatar'
                        }
                        style = {
                            {
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                            }
                        }
                        />
                    ) : ( <div style = {
                            {
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255,255,255,0.78)',
                                fontWeight: 700,
                                textAlign: 'center',
                                padding: 12
                            }
                        } >
                        {rglProfileState.status === 'loading' ? 'Loading profile...' : 'Profile image unavailable'}
                        </div>
                    )
                } </div>

                <div style = {
                    {
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr auto',
                        gap: 16,
                        minWidth: 0
                    }
                } >
                <div>
                <h2 style = {
                    {
                        margin: 0,
                        fontSize: 'clamp(34px, 3.6vw, 48px)',
                        lineHeight: 1,
                        textAlign: 'center'
                    }
                } >
                {rglProfileState.status === 'success' && rglProfileData && rglProfileData.name ? rglProfileData.name : "Ari's RGL Profile"}
                </h2>
                <div style = {
                    {
                        marginTop: 14,
                        display: 'grid',
                        gap: 10,
                        color: 'rgba(255,255,255,0.92)',
                        justifyItems: 'center',
                        textAlign: 'center',
                        fontSize: 'clamp(18px, 1.6vw, 24px)',
                        lineHeight: 1.2
                    }
                } >
                {
                    rglProfileState.status === 'loading' ? ( <span>Fetching current Highlander team...</span> ) :
                    rglProfileState.status === 'error' ? ( <span>Could not load live profile data. You can still open the RGL page directly.</span> ) :
                    ( <>
                        <span><strong style = {{ color: '#ffbf6a' }}>Current Team:</strong> {rglPrimaryTeam && rglPrimaryTeam.name ? rglPrimaryTeam.name : 'No active team found'}</span>
                        <span><strong style = {{ color: '#ffbf6a' }}>Current Div:</strong> {rglPrimaryTeam && rglPrimaryTeam.divisionName ? `${rglPrimaryTeamFormat} - ${formatRglDivisionName(rglPrimaryTeam.divisionName)}` : 'No active division listed'}</span>
                        <span><strong style = {{ color: '#ffbf6a' }}>Probation:</strong> {rglProfileProbationLabel}</span>
                        <span><strong style = {{ color: '#ffbf6a' }}>Banned:</strong> {rglProfileBanLabel}</span>
                    </> )
                }
                </div>
                </div>

                <div style = {
                    {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }
                } >
                <button type = "button"
                className = "rgl-rhombus-button"
                onClick = {
                    () =>
                    {
                        try
                        {
                            window.open(buildRglProfileUrl(rglProfileData && rglProfileData.steamId ? rglProfileData.steamId : rglProfileSteamId), '_blank', 'noopener,noreferrer');
                        }
                        catch (e)
                        {}
                    }
                } >
                <span className = "rgl-rhombus-button__label" > Visit RGL Profile </span> </button> </div>

                <div style = {
                    {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.62)',
                        textAlign: 'right'
                    }
                } >
                {rglProfileState.status === 'success' ? rglProfileUpdatedLabel : (rglProfileState.status === 'error' ? 'Last updated: unavailable' : 'Last updated: loading...')}
                </div> </div> </div>
            ),
            customFooter: ( <form onSubmit = {
                    (event) =>
                    {
                        event.preventDefault();
                        const nextSteamId = String(rglProfileSearchInput || '').trim();
                        if (!nextSteamId || nextSteamId === rglProfileSteamId) return;
                        setRglProfileSteamId(nextSteamId);
                        setRglProfileState(
                        {
                            status: 'idle',
                            data: null,
                            error: ''
                        });
                    }
                }
                style = {
                    {
                        marginTop: 14,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        padding: 12,
                        borderRadius: 14,
                        background: 'rgba(0,0,0,0.48)',
                        border: '1px solid rgba(255,145,0,0.28)',
                        width: 'min(560px, 72%)',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        gap: 10,
                        alignItems: 'center'
                    }
                } >
                <input type = "text"
                value = {
                    rglProfileSearchInput
                }
                onChange = {
                    (event) => setRglProfileSearchInput(event.target.value)
                }
                placeholder = "Enter someone's Steam ID"
                inputMode = "numeric"
                spellCheck = {
                    false
                }
                style = {
                    {
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '12px 14px',
                        borderRadius: 10,
                        border: '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.06)',
                        color: '#fff',
                        fontSize: 16,
                        outline: 'none'
                    }
                }
                />
                <button type = "submit"
                style = {
                    {
                        padding: '12px 22px',
                        borderRadius: 999,
                        border: '1px solid rgba(255,145,0,0.45)',
                        background: 'linear-gradient(135deg, rgba(255,145,0,0.3), rgba(130,70,0,0.55))',
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 800,
                        cursor: 'pointer',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
                        textShadow: '0 2px 2px rgba(0,0,0,0.5)'
                    }
                } >
                Search User </button> </form>
            )
        } : activeInfoPanel === 'live' ?
        {
            title: 'Watch me fucking die over and over',
            body: ( <>
                Ive given it a go recently to stream our scrim games so people can watch our team get fucked live through me!For now its just practice games, but well get to the actual official games soon. <br /> <br />
                The actual week - by - week match footage still lives in the season entries below, this is more of an option to see yourself how i play as a medic in this team or
                if you just wanna see shit go down on other players lmao <br /> <br />
                There is a schdule i tend to follow
                for when scrims happens and thats usually what im gonna apply to the twitch schedule channel.Some of you in RGL know what im talking about.You can find it within my twitch page cuz adding it here is too much work and i dont wanna constantly update 2 things at once. </>
            ),
            imageSrc: '/RGL Images/TwitchIcon.png',
            imageAlt: 'Twitch',
            imageStyle:
            {
                display: 'block',
                margin: '20px auto 0',
                width: 120,
                maxWidth: '28%',
                height: 'auto'
            },
            action:
            {
                label: 'Open Channel',
                onClick: () =>
                {
                    try
                    {
                        window.open('https://www.twitch.tv/arielwolf24', '_blank', 'noopener,noreferrer');
                    }
                    catch (e)
                    {}
                }
            }
        } :
        {
            title: 'What the fuck is RGL.gg??',
            body: ( <>
                RGL is a unofficial league that host competitive tournaments in Team Fortress 2. This is more of a 3 rd Party, however you can earn unique medals
                for placing top 3 in a division or by just participating. <br /> <br />
                The standard official competitve mode within the game is very dead
                for a multitide of reasons so
                if youre looking to get youtself into competitive play, RGL is a great option. <br /> <br />
                The 2 types of competitive play youll commonly see in the league is Sixes and Highlander, however
                for the sake of this page we will focus on Highlander. <br /> <br />
                Highlander is basically a 9 v9 format where each player picks any class on the roster and there can only be 1 of each class.As you may have seen, I main Medic and i usually go as insane as expected. <br /> <br />
                The shit youll see here is my past experience and history through out the seasons, the site will filter to the most recent season but you can still look back at the previous ones. <br /> <br />
                Looking through the weeks, some will come with logs
                for you to look at
                for a specific match, some with a youtube button to see my recorded game through the eyes of a medic, or
                if you wanna see it yourself within in game, some will come with a demo file or with a button that looks like a blue sticky bomb.That however youll have to figure out yourself, but it is a thing in TF2
                if you didnt know. <br /> <br />
                Anyway it may look a bit vague on information atm but im still looking to see what stuff i can add related to my RGL experience, so more stuff coming soon!
                </>
            ),
            imageSrc: '/RedLeo.png',
            imageAlt: 'Red Leo',
            imageStyle:
            {
                display: 'block',
                margin: '20px auto 0',
                width: 320,
                maxWidth: '35%',
                height: 'auto',
                borderRadius: 6
            }
        };

    return ( <div style = {
            {
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                pointerEvents: 'auto'
            }
        } >
        {
            /* preloaded OPEN video, underneath */ } <video ref = {
            bottomRef
        }
        src = "/Video/TF2_CompDoors-OPEN.mp4"
        style = {
            {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 9999,
                pointerEvents: 'none'
            }
        }
        playsInline preload = "auto"
        onEnded = {
            onOpenEnded
        }
        />

        {
            /* CLOSE video shown on entry, removed when swappedToOpen */ }
        {
            !swappedToOpen && ( <video ref = {
                    topRef
                }
                src = "/Video/TF2_CompDoors-CLOSE.mp4"
                style = {
                    {
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 10000
                    }
                }
                playsInline autoPlay onEnded = {
                    onClosedEnded
                }
                onPlay = {
                    () =>
                    {
                        const revealToken = rglRevealTokenRef.current + 1;
                        rglRevealTokenRef.current = revealToken;
                        if (uiTimerRef.current) clearTimeout(uiTimerRef.current);
                        clearRglPreloadNotice();
                        uiTimerRef.current = setTimeout(async () =>
                        {
                            uiTimerRef.current = null;
                            preloadNoticeTimerRef.current = setTimeout(() =>
                            {
                                preloadNoticeTimerRef.current = null;
                                if (rglRevealTokenRef.current !== revealToken) return;
                                setShowPreloadNotice(true);
                            }, RGL_PRELOAD_NOTICE_DELAY_MS);
                            try
                            {
                                await preloadRglAudioAssets();
                            }
                            catch (e)
                            {}
                            if (preloadNoticeTimerRef.current)
                            {
                                clearTimeout(preloadNoticeTimerRef.current);
                                preloadNoticeTimerRef.current = null;
                            }
                            if (rglRevealTokenRef.current !== revealToken) return;
                            setShowPreloadNotice(false);
                            setShowUI(true);
                        }, 1000);
                    }
                }
                />
            )
        }
        {
            !swappedToOpen && showPreloadNotice && !showUI && ( <div style = {
                    {
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 10002
                    }
                } >
                <div style = {
                    {
                        padding: '14px 22px',
                        background: 'rgba(10, 12, 16, 0.9)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.32)',
                        color: '#f4f7fb',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                    }
                } >
                Preloading RGL assets...
                </div> </div>
            )
        }
        {
            /* Grouped overlay: background, icon, label and return button - show together; keep visible during outro */ }
        {
            (!swappedToOpen || outroAnimating) && ( <div style = {
                    {
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: (showUI && !outroAnimating) ? 'auto' : 'none',
                        zIndex: 10001,
                        opacity: (showUI || outroAnimating) ? 1 : 0,
                        transition: 'opacity 120ms linear'
                    }
                } >
                {
                    /* centered stack to lock positions on resize */ } <div ref = {
                    groupRef
                }
                style = {
                    {
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: (outroAnimating ? 'translateY(-120vh)' : (moveToTop ? `translateY(-${moveUpPx}px)` : 'translateY(0)')),
                        transition: 'transform 180ms ease-in'
                    }
                } >
                <div style = {
                    {
                        position: 'relative',
                        transform: `scale(${topTabFitScale})`,
                        transformOrigin: 'center center'
                    }
                } >
                {
                    /* background behind the group, centered and sliding from left */ } <img src = "/BG-RGL_Icon.png"
                alt = ""
                style = {
                    {
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: (showUI ? 'translate(-50%, -50%) translateX(0)' : 'translate(-50%, -50%) translateX(-120vw)'),
                        transition: 'transform 200ms linear',
                        width: `min(${backgroundPlateWidth}px, 92vw)`,
                        height: 'auto',
                        zIndex: 10000,
                        pointerEvents: 'none'
                    }
                }
                />

                {
                    /* extending bar behind the assets; scales in horizontally and exceeds viewport width */ } <div style = {
                    {
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: showBar ? 'translate(-50%, -50%) scaleX(1)' : 'translate(-50%, -50%) scaleX(0)',
                        transformOrigin: 'center',
                        width: `max(${Math.max(260, viewportWidth * 3)}px, 320vw)`,
                        height: barHeight,
                        background: 'rgba(0, 0, 0, 0.6)',
                        borderTop: '2px solid #000',
                        borderBottom: '2px solid #000',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
                        zIndex: 10001,
                        opacity: showBar ? 1 : 0,
                        transition: 'transform 240ms ease-out, opacity 240ms ease-out',
                        pointerEvents: 'none'
                    }
                }
                />

                {
                    /* bar overlay controls: left and right groups of text-only buttons */ }
                {
                    showBar && ( <div style = {
                            {
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: `translate(calc(-50% + ${topBarButtonsShiftPx}px), -50%)`,
                                zIndex: 10003,
                                pointerEvents: 'auto',
                            }
                        } >
                        <div ref = {
                            topBarLayoutRef
                        }
                        style = {
                            {
                                position: 'relative',
                                width: 'max-content',
                                height: barHeight,
                                padding: `0 ${barPaddingX}px`,
                            }
                        } >
                        <div style = {
                            {
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: `translate(calc(-100% - ${Math.round(topBarCenterGap / 2)}px), -50%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: topBarGroupGap,
                                width: 'max-content',
                                flexShrink: 0,
                                minWidth: 0,
                                pointerEvents: 'auto'
                            }
                        } >
                        <button onClick = {
                            onReturnClick
                        }
                        type = "button"
                        className = "rgl-rhombus-button"
                        style = {
                            {
                                '--rgl-button-scale': overlayScale
                            }
                        } >
                        <span className = "rgl-rhombus-button__label" > Return <br /> to main </span> </button>

                        <button type = "button"
                        className = "rgl-rhombus-button"
                        style = {
                            {
                                '--rgl-button-scale': overlayScale
                            }
                        }
                        onClick = {
                            () =>
                            {
                                try
                                {
                                    window.open('https://rgl.gg/Public/about/FrontPage/Intro/default', '_blank', 'noopener,noreferrer');
                                }
                                catch (e)
                                {}
                            }
                        } >
                        <span className = "rgl-rhombus-button__label" > What the fuck <br /> is RGL?? </span> </button> </div>

                        <div style = {
                            {
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: `translate(${Math.round(topBarCenterGap / 2)}px, -50%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: topBarGroupGap,
                                width: 'max-content',
                                flexShrink: 0,
                                minWidth: 0,
                                pointerEvents: 'auto'
                            }
                        } >
                        <button onClick = {
                            () =>
                            {
                                try
                                {
                                    window.open('https://rgl.gg/Public/Team?t=15314&r=24', '_blank', 'noopener,noreferrer');
                                }
                                catch (e)
                                {}
                            }
                        }
                        type = "button"
                        className = "rgl-rhombus-button"
                        style = {
                            {
                                '--rgl-button-scale': overlayScale
                            }
                        } >
                        <span className = "rgl-rhombus-button__label" > Team <br /> P.T.U </span> </button>

                        <button onClick = {
                            () => setActiveInfoPanel('profile')
                        }
                        type = "button"
                        className = "rgl-rhombus-button"
                        style = {
                            {
                                '--rgl-button-scale': overlayScale
                            }
                        } >
                        <span className = "rgl-rhombus-button__label" > View<br/>Profile</span> </button>

                        <button type = "button"
                        className = "rgl-rhombus-button"
                        style = {
                            {
                                '--rgl-button-scale': overlayScale
                            }
                        }
                        onClick = {
                            () => setActiveInfoPanel('live')
                        } >
                        <span className = "rgl-rhombus-button__label" > Watch me <br /> on Twitch! </span> </button> </div> </div> </div>
                    )
                }

                {
                    /* content column, centered; only icon+text animate from right */ } <div style = {
                    {
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: RGL_BASE_CONTENT_GAP,
                        zIndex: 10002
                    }
                } >
                <div ref = {
                    contentRef
                }
                style = {
                    {
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: RGL_BASE_CONTENT_WRAP_GAP,
                        maxWidth: '92vw',
                        transform: showUI ? 'translateX(0)' : 'translateX(120vw)',
                        transition: 'transform 200ms linear'
                    }
                } >
                <div style = {
                    {
                        pointerEvents: 'none'
                    }
                } >
                <RGLIcon basePath = "/RGL-Icon"
                size = {
                    iconSize
                }
                fps = {
                    30
                }
                /> </div> <div style = {
                    {
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: rglTitleFontSize,
                        transform: 'translate(-0%,-5%)',
                        textShadow: '0 3px 2px rgba(0,0,0,1)',
                        pointerEvents: 'none',
                        lineHeight: 1.05,
                        textAlign: 'center'
                    }
                } > RGL.gg </div> </div>

                {
                    /* temporary: remove subtext below the icon while keeping layout intact */ }

                {
                    /* temporarily hidden: keep button code for later reuse */ }
                {
                    /* <div>
                                    <button
                                      onClick={onReturnClick}
                                      style={{ padding: '12px 22px', borderRadius: 10, background: 'rgba(0, 0, 0, 0.42)', border: '2px solid #000', color: '#ffffffff', cursor: 'pointer', fontWeight: 700, fontSize: 18, boxShadow: '0 6px 16px rgba(0,0,0,0.35)', opacity: 0.95 }}
                                    >
                                      Return to /main
                                    </button>
                                  </div> */
                } </div> </div> </div> </div>
            )
        }

        {
            /* Separate UI layer for week + details panel (in front of the bar layer) */ }
        {
            (showBar || (outroAnimating && panelEverShown)) && ( <div style = {
                    {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 10004,
                        pointerEvents: 'none'
                    }
                } >
                <div style = {
                    {
                        position: 'fixed',
                        left: '50%',
                        top: (typeof panelTopPx === 'number') ? `${panelTopPx}px` : '58%',
                        transform: (outroAnimating && panelEverShown) ?
                            'translateX(-50%) translateY(120vh)' :
                            'translateX(-50%) translateY(0)',
                        width: 'min(1050px, 92vw)',
                        height: 'clamp(450px, 70vh, 850px)',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: '2px solid #000',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
                        opacity: (outroAnimating && panelEverShown) ? 1 : (showPanel ? 1 : 0),
                        transition: (outroAnimating && panelEverShown) ?
                            'transform 320ms ease-in' :
                            'opacity 220ms ease-out',
                        pointerEvents: (showPanel && !outroAnimating) ? 'auto' : 'none',
                        padding: 'clamp(10px, 1.4vw, 14px)',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(240px, 34%) 1fr',
                        gap: 'clamp(10px, 1.4vw, 14px)',
                        backdropFilter: 'blur(3px)',
                        alignItems: 'stretch',
                        willChange: 'transform, opacity'
                    }
                } >
                {
                    /* Left: season + week list */ } <div style = {
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        minWidth: 0,
                        minHeight: 0
                    }
                } >
                <div style = {
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                    }
                } >
                <div style = {
                    {
                        color: '#fff',
                        fontWeight: 800,
                        textShadow: '0 3px 2px rgba(0,0,0,1)'
                    }
                } > Season </div> <select className = "btn-primary"
                value = {
                    selectedSeasonId
                }
                onChange = {
                    (e) => setSelectedSeasonId(e.target.value)
                }
                style = {
                    {
                        width: '100%',
                        fontSize: 'clamp(0.92rem, 1.6vw, 1.02rem)',
                        padding: 'clamp(10px, 1.2vw, 12px) clamp(12px, 1.6vw, 16px)',
                        cursor: 'pointer'
                    }
                } >
                {
                    seasons.map(s => ( <option key = {
                            s.id
                        }
                        value = {
                            s.id
                        }
                        style = {
                            {
                                color: '#000'
                            }
                        } >
                        {
                            s.label
                        } </option>
                    ))
                } </select> </div>

                <div style = {
                    {
                        color: '#fff',
                        fontWeight: 800,
                        textShadow: '0 3px 2px rgba(0,0,0,1)'
                    }
                } > Weeks </div> <div style = {
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        overflowY: 'auto',
                        paddingRight: 6,
                        flex: 1,
                        minHeight: 0
                    }
                } >
                {
                    weeks.map(w =>
                    {
                        const active = w.id === selectedWeekId;
                        return ( <button key = {
                                w.id
                            }
                            type = "button"
                            className = "btn-primary"
                            onClick = {
                                () => setSelectedWeekId(w.id)
                            }
                            style = {
                                {
                                    width: '100%',
                                    textAlign: 'left',
                                    fontSize: 'clamp(0.92rem, 1.6vw, 1.02rem)',
                                    padding: 'clamp(10px, 1.2vw, 12px) clamp(12px, 1.6vw, 16px)',
                                    background: active ? '#ffffff25' : undefined,
                                    borderColor: active ? '#ffffff80' : undefined
                                }
                            } >
                            {
                                w.label
                            } </button>
                        );
                    })
                } </div> </div>

                {
                    /* Right: details panel */ } <div style = {
                    {
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        padding: 'clamp(10px, 1.4vw, 14px)',
                        background: 'rgba(0,0,0,0.35)',
                        border: '1px solid #000',
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
                        overflow: 'auto',
                        minHeight: 0
                    }
                } >
                <div style = {
                    {
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 'clamp(18px, 2.2vw, 26px)',
                        textShadow: '0 3px 2px rgba(0,0,0,1)'
                    }
                } >
                Details </div>
                {
                    selectedSeasonId && selectedWeekId && (() =>
                    {
                        const season = seasons.find(s => s.id === selectedSeasonId);
                        const week = weeks.find(w => w.id === selectedWeekId);
                        const homeName = week?.details?.friendlyTeam || 'FR*NC';
                        const opponentName = week?.details?.opponent || 'TBD';
                        const opponentIcon = week?.details?.opponentIcon;
                        const homeIcon = week?.details?.friendlyIcon || (opponentIcon ? 'Frnc.png' : '');
                        const hasTeamIcons = Boolean(homeIcon || opponentIcon);
                        const scaleFor = (name) =>
                        {
                            if (name.length > 28) return 0.75;
                            if (name.length > 20) return 0.85;
                            return 1;
                        };
                        const homeScale = scaleFor(homeName);
                        const opponentScale = scaleFor(opponentName);
                        const mapKeys = ['map', 'map2', 'map3', 'map4', 'map5'];
                        const mapEntries = mapKeys
                            .map((key) => week.details[key])
                            .filter(Boolean)
                            .filter((mapName) => mapName !== 'TBD');
                        const detailsFieldKeys = new Set([
                            'friendlyTeam',
                            'friendlyIcon',
                            'opponent',
                            'opponentIcon',
                            'score',
                            'matchDetails',
                            'map',
                            'map2',
                            'map3',
                            'map4',
                            'map5',
                            'youtubeLink',
                            'downloadLink',
                            'logsLink'
                        ]);
                        const extraDetails = Object.entries(week.details || {})
                            .filter(([key, value]) => !detailsFieldKeys.has(key) && value && typeof value === 'string');
                        return ( <>
                            <div style = {
                                {
                                    color: '#ffffffd9',
                                    fontWeight: 700
                                }
                            } > {season ? season.label : 'Season'} — {week ? week.label : 'Week'} </div>
                            {
                                week && week.details && ( <div style = {
                                        {
                                            color: '#ffffffc0',
                                            lineHeight: 1.35,
                                            textAlign: 'left',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 16
                                        }
                                    } >
                                    <div style = {
                                        {
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto 1fr',
                                            alignItems: 'center',
                                            columnGap: 12,
                                            fontWeight: 800,
                                            color: '#fff',
                                            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                                            letterSpacing: 1,
                                            fontSize: 'clamp(18px, 2.6vw, 30px)',
                                            fontFamily: 'Oxygen, sans-serif'
                                        }
                                    } >
                                    <div style = {
                                        {
                                            textAlign: 'center',
                                            minWidth: 0,
                                            maxWidth: '100%',
                                            lineHeight: 1.1,
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            transform: `scale(${homeScale})`,
                                            transformOrigin: 'center'
                                        }
                                    } >
                                    {
                                        homeName
                                    } </div> <div style = {
                                        {
                                            textAlign: 'center',
                                            color: '#ffffffb0',
                                            fontWeight: 700
                                        }
                                    } >
                                    VS </div> <div style = {
                                        {
                                            textAlign: 'center',
                                            minWidth: 0,
                                            maxWidth: '100%',
                                            lineHeight: 1.1,
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            transform: `scale(${opponentScale})`,
                                            transformOrigin: 'center'
                                        }
                                    } >
                                    {
                                        opponentName
                                    } </div> </div>
                                    {
                                        /* Score display with team icons */ }
                                    {
                                        hasTeamIcons && ( <div style = {
                                                {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 24,
                                                    padding: '8px 0'
                                                }
                                            } >
                                            {
                                                /* Friendly team icon */ }
                                            {
                                                homeIcon && ( <img src = {
                                                        `/RGL Images/Teams/${homeIcon}`
                                                    }
                                                    alt = {
                                                        homeName
                                                    }
                                                    style = {
                                                        {
                                                            width: 'clamp(90px, 9vw, 120px)',
                                                            height: 'clamp(90px, 9vw, 120px)',
                                                            objectFit: 'contain'
                                                        }
                                                    }
                                                    onError = {
                                                        (e) =>
                                                        {
                                                            e.target.style.display = 'none';
                                                        }
                                                    }
                                                    />
                                                )
                                            }

                                            {
                                                /* Score */ } <div style = {
                                                {
                                                    fontSize: 'clamp(32px, 4vw, 48px)',
                                                    fontWeight: 900,
                                                    color: '#fff',
                                                    textShadow: '0 3px 6px rgba(0,0,0,0.8)',
                                                    letterSpacing: 2
                                                }
                                            } >
                                            {
                                                week.details.score || '0 - 0'
                                            } </div>

                                            {
                                                /* Opponent team icon */ }
                                            {
                                                opponentIcon && ( <img src = {
                                                        `/RGL Images/Teams/${opponentIcon}`
                                                    }
                                                    alt = {
                                                        week.details.opponent || 'Opponent'
                                                    }
                                                    style = {
                                                        {
                                                            width: 'clamp(90px, 9vw, 120px)',
                                                            height: 'clamp(90px, 9vw, 120px)',
                                                            objectFit: 'contain'
                                                        }
                                                    }
                                                    onError = {
                                                        (e) =>
                                                        {
                                                            e.target.style.display = 'none';
                                                        }
                                                    }
                                                    />
                                                )
                                            } </div>
                                        )
                                    }

                                    {
                                        /* Score display without icons */ }
                                    {
                                        !hasTeamIcons && ( <div style = {
                                                {
                                                    fontSize: 'clamp(32px, 4vw, 48px)',
                                                    fontWeight: 900,
                                                    color: '#fff',
                                                    textShadow: '0 3px 6px rgba(0,0,0,0.8)',
                                                    textAlign: 'center',
                                                    letterSpacing: 2,
                                                    padding: '16px 0'
                                                }
                                            } >
                                            {
                                                week.details.score || '0 - 0'
                                            } </div>
                                        )
                                    }

                                    {
                                        week.details.matchDetails && ( <div style = {
                                                {
                                                    width: '100%',
                                                    padding: '12px 14px',
                                                    borderRadius: 8,
                                                    background: 'rgba(0, 0, 0, 0.34)',
                                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                                    color: '#fff',
                                                    textAlign: 'center',
                                                    whiteSpace: 'pre-wrap',
                                                    overflowWrap: 'anywhere',
                                                    lineHeight: 1.45,
                                                    fontSize: 'clamp(0.95rem, 1.7vw, 1.05rem)',
                                                    boxSizing: 'border-box'
                                                }
                                            } >
                                            {
                                                week.details.matchDetails
                                            } </div>
                                        )
                                    }

                                    {
                                        extraDetails.length > 0 && ( <div style = {
                                                {
                                                    display: 'grid',
                                                    gap: 10,
                                                    width: '100%'
                                                }
                                            } >
                                            {
                                                extraDetails.map(([key, value]) => ( <div key = {
                                                        key
                                                    }
                                                    style = {
                                                        {
                                                            width: '100%',
                                                            padding: '12px 14px',
                                                            borderRadius: 8,
                                                            background: 'rgba(0, 0, 0, 0.24)',
                                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                                            color: '#fff',
                                                            lineHeight: 1.4,
                                                            fontSize: 'clamp(0.95rem, 1.7vw, 1.05rem)',
                                                            boxSizing: 'border-box'
                                                        }
                                                    } >
                                                    <span style = {
                                                        {
                                                            fontWeight: 700,
                                                            marginRight: 6
                                                        }
                                                    } >
                                                    {
                                                        key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                                                    }:
                                                    </span>
                                                    {
                                                        value
                                                    } </div>
                                                ))
                                            }
                                        </div>
                                    )
                                    }

                                    {
                                        mapEntries.length > 0 && ( <div style = {
                                                {
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 12
                                                }
                                            } >
                                            {
                                                mapEntries.map((mapName, index) => ( <div key = {
                                                        `${mapName}-${index}`
                                                    }
                                                    style = {
                                                        {
                                                            position: 'relative',
                                                            width: '100%',
                                                            borderRadius: 6,
                                                            overflow: 'hidden'
                                                        }
                                                    } >
                                                    <img src = {
                                                        `/RGL Images/Maps/${mapName}.png`
                                                    }
                                                    alt = {
                                                        mapName
                                                    }
                                                    style = {
                                                        {
                                                            width: '100%',
                                                            height: 'auto',
                                                            maxHeight: '200px',
                                                            objectFit: 'cover',
                                                            display: 'block'
                                                        }
                                                    }
                                                    onError = {
                                                        (e) =>
                                                        {
                                                            e.target.style.display = 'none';
                                                        }
                                                    }
                                                    /> <div style = {
                                                        {
                                                            position: 'absolute',
                                                            left: '50%',
                                                            top: 6,
                                                            transform: 'translateX(-50%)',
                                                            padding: '4px 10px',
                                                            borderRadius: 6,
                                                            background: 'rgba(0,0,0,0.45)',
                                                            color: '#fff',
                                                            fontWeight: 800,
                                                            fontSize: 'clamp(12px, 1.6vw, 16px)',
                                                            textShadow: '0 2px 6px rgba(0,0,0,0.9)',
                                                            letterSpacing: 1
                                                        }
                                                    } >
                                                    {
                                                        mapName
                                                    } </div> </div>
                                                ))
                                            }
                                            {
                                                (week.details.youtubeLink || week.details.downloadLink || week.details.logsLink) && ( <div style = {
                                                        {
                                                            display: 'flex',
                                                            gap: 6,
                                                            padding: 8,
                                                            background: 'rgba(0,0,0,0.2)',
                                                            borderRadius: 6
                                                        }
                                                    } >
                                                    {
                                                        week.details.youtubeLink && ( <a href = {
                                                                week.details.youtubeLink
                                                            }
                                                            target = "_blank"
                                                            rel = "noopener noreferrer"
                                                            style = {
                                                                {
                                                                    display: 'block',
                                                                    width: 40,
                                                                    height: 40,
                                                                    padding: 6,
                                                                    background: 'rgba(0,0,0,0.65)',
                                                                    borderRadius: 6,
                                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease'
                                                                }
                                                            }
                                                            onMouseEnter = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(255,0,0,0.7)';
                                                                }
                                                            }
                                                            onMouseLeave = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                                }
                                                            } >
                                                            <img src = "/RGL Images/YoutubeIcon.png"
                                                            alt = "YouTube"
                                                            style = {
                                                                {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain'
                                                                }
                                                            }
                                                            /> </a>
                                                        )
                                                    }
                                                    {
                                                        week.details.downloadLink && ( <a href = {
                                                                week.details.downloadLink
                                                            }
                                                            target = "_blank"
                                                            rel = "noopener noreferrer"
                                                            style = {
                                                                {
                                                                    display: 'block',
                                                                    width: 40,
                                                                    height: 40,
                                                                    padding: 6,
                                                                    background: 'rgba(0,0,0,0.65)',
                                                                    borderRadius: 6,
                                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease'
                                                                }
                                                            }
                                                            onMouseEnter = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                                                                }
                                                            }
                                                            onMouseLeave = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                                }
                                                            } >
                                                            <img src = "/RGL Images/DemoManIcon.png"
                                                            alt = "Download"
                                                            style = {
                                                                {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain'
                                                                }
                                                            }
                                                            /> </a>
                                                        )
                                                    }
                                                    {
                                                        week.details.logsLink && ( <a href = {
                                                                week.details.logsLink
                                                            }
                                                            target = "_blank"
                                                            rel = "noopener noreferrer"
                                                            style = {
                                                                {
                                                                    display: 'block',
                                                                    width: 75,
                                                                    height: 40,
                                                                    padding: 6,
                                                                    background: 'rgba(0,0,0,0.65)',
                                                                    borderRadius: 6,
                                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease'
                                                                }
                                                            }
                                                            onMouseEnter = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(220,140,40,0.7)';
                                                                }
                                                            }
                                                            onMouseLeave = {
                                                                (e) =>
                                                                {
                                                                    e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                                }
                                                            } >
                                                            <img src = "/RGL Images/LogsTF-Icon.png"
                                                            alt = "Logs.tf"
                                                            style = {
                                                                {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain'
                                                                }
                                                            }
                                                            /> </a>
                                                        )
                                                    } </div>
                                                )
                                            }
                                        </div>
                                    )
                                    }

                                    {
                                        !mapEntries.length && (week.details.youtubeLink || week.details.downloadLink || week.details.logsLink) && ( <div style = {
                                                {
                                                    display: 'flex',
                                                    gap: 6,
                                                    padding: 8,
                                                    background: 'rgba(0,0,0,0.2)',
                                                    borderRadius: 6
                                                }
                                            } >
                                            {
                                                week.details.youtubeLink && ( <a href = {
                                                        week.details.youtubeLink
                                                    }
                                                    target = "_blank"
                                                    rel = "noopener noreferrer"
                                                    style = {
                                                        {
                                                            display: 'block',
                                                            width: 40,
                                                            height: 40,
                                                            padding: 6,
                                                            background: 'rgba(0,0,0,0.65)',
                                                            borderRadius: 6,
                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }
                                                    }
                                                    onMouseEnter = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(255,0,0,0.7)';
                                                        }
                                                    }
                                                    onMouseLeave = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                        }
                                                    } >
                                                    <img src = "/RGL Images/YoutubeIcon.png"
                                                    alt = "YouTube"
                                                    style = {
                                                        {
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain'
                                                        }
                                                    }
                                                    /> </a>
                                                )
                                            }
                                            {
                                                week.details.downloadLink && ( <a href = {
                                                        week.details.downloadLink
                                                    }
                                                    target = "_blank"
                                                    rel = "noopener noreferrer"
                                                    style = {
                                                        {
                                                            display: 'block',
                                                            width: 40,
                                                            height: 40,
                                                            padding: 6,
                                                            background: 'rgba(0,0,0,0.65)',
                                                            borderRadius: 6,
                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }
                                                    }
                                                    onMouseEnter = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                                                        }
                                                    }
                                                    onMouseLeave = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                        }
                                                    } >
                                                    <img src = "/RGL Images/DemoManIcon.png"
                                                    alt = "Download"
                                                    style = {
                                                        {
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain'
                                                        }
                                                    }
                                                    /> </a>
                                                )
                                            }
                                            {
                                                week.details.logsLink && ( <a href = {
                                                        week.details.logsLink
                                                    }
                                                    target = "_blank"
                                                    rel = "noopener noreferrer"
                                                    style = {
                                                        {
                                                            display: 'block',
                                                            width: 75,
                                                            height: 40,
                                                            padding: 6,
                                                            background: 'rgba(0,0,0,0.65)',
                                                            borderRadius: 6,
                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }
                                                    }
                                                    onMouseEnter = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(220,140,40,0.7)';
                                                        }
                                                    }
                                                    onMouseLeave = {
                                                        (e) =>
                                                        {
                                                            e.currentTarget.style.background = 'rgba(0,0,0,0.65)';
                                                        }
                                                    } >
                                                    <img src = "/RGL Images/LogsTF-Icon.png"
                                                    alt = "Logs.tf"
                                                    style = {
                                                        {
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain'
                                                        }
                                                    }
                                                    /> </a>
                                                )
                                            } </div>
                                        )
                                    } </div>
                                )
                            } </>
                        );
                    })()
                } </div> </div> </div>
            )
        }

        {
            activeInfoPanel && ( <div style = {
                    {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 20050,
                        pointerEvents: 'auto',
                        background: 'rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(6px)'
                    }
                } >
                <div style = {
                    {
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${infoModalPaddingPx}px`,
                        overflow: 'hidden'
                    }
                } >
                <div ref = {
                    infoModalRef
                }
                style = {
                    {
                        position: 'relative',
                        maxWidth: 720,
                        width: 'min(720px, 80vw)',
                        boxSizing: 'border-box',
                        padding: 24,
                        background: 'rgba(10,10,10,0.85)',
                        color: '#fff',
                        borderRadius: 12,
                        transform: `scale(${infoScale})`,
                        transformOrigin: 'center center',
                        visibility: infoScaleReady ? 'visible' : 'hidden',
                        ...(infoModalContent.modalStyle || {})
                    }
                } >
                {
                    /* Close button in the very corner of the box */ } <button type = "button"
                onClick = {
                    () => setActiveInfoPanel(null)
                }
                aria-label = "Close information"
                style = {
                    {
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'transparent',
                        border: '1px solid #ffffff55',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 800,
                        lineHeight: '30px',
                        textAlign: 'center'
                    }
                } >
                ×
                </button>

                {
                    infoModalContent.customBody ? infoModalContent.customBody : ( <>
                        {
                            /* Image at the top-right of the box */ }
                        {
                            /* Title and description content */ } <h2 style = {
                            {
                                margin: 0,
                                textAlign: 'center'
                            }
                        } >
                        {
                            infoModalContent.title
                        } </h2> <p style = {
                            {
                                marginTop: 10,
                                opacity: 0.95
                            }
                        } >
                        {
                            infoModalContent.body
                        } </p>

                        {
                            infoModalContent.action && ( <div style = {
                                    {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: 18
                                    }
                                } >
                                <button type = "button"
                                className = "rgl-rhombus-button"
                                onClick = {
                                    infoModalContent.action.onClick
                                } >
                                <span className = "rgl-rhombus-button__label" >
                                {
                                    infoModalContent.action.label
                                } </span> </button> </div>
                            )
                        }

                        <img src = {
                            infoModalContent.imageSrc
                        }
                        alt = {
                            infoModalContent.imageAlt
                        }
                        style = {
                            infoModalContent.imageStyle
                        }
                        /> </>
                    )
                }

                {
                    infoModalContent.customFooter || null
                } </div> </div> </div>
            )
        } </div>
    );
}
